import { CONFIG } from '../constants/config';
import { getMoonPhase, getMoonTimes } from '../utils/moonPhase';
import { formatDateForAPI } from '../utils/dateHelpers';
import { fetchWithTimeout, buildWeatherUrl, buildTideUrl, buildWaterTempUrl, buildWaterLevelUrl } from '../utils/api';
import { getFromStorage, setToStorage, isCacheValid } from '../utils/localStorage';

class WeatherService {
  // Check if cached data is available and valid
  async checkCache() {
    try {
      const cached = getFromStorage(CONFIG.CACHE_KEY);
      if (cached && isCacheValid(cached, CONFIG.CACHE_DURATION)) {
        return cached.data;
      }
    } catch (e) {
      // Cache read error silently handled
    }
    return null;
  }

  /**
   * Fetch weather data from Open-Meteo API
   * Uses a two-step approach: simple test request, then full data
   * @param {number} lat - Latitude
   * @param {number} lon - Longitude
   * @returns {Object} Weather data object
   */
  async fetchWeatherData(lat, lon) {
    // Start with a simpler request to test API availability
    // This helps identify connection issues quickly
    const simpleWeatherUrl = buildWeatherUrl(lat, lon, {
      current: 'temperature_2m,weather_code'
    });
    
    const simpleResponse = await fetchWithTimeout(simpleWeatherUrl, CONFIG.WEATHER_API_TIMEOUT);
    
    if (!simpleResponse.ok) {
      const errorText = await simpleResponse.text();
      throw new Error(`Simple weather request failed: ${simpleResponse.status} - ${errorText}`);
    }
    
    // If simple request works, fetch comprehensive weather data
    // Includes current conditions, 24-hour forecast, and 7-day forecast
    const weatherUrl = buildWeatherUrl(lat, lon, {
      current: 'temperature_2m,relative_humidity_2m,apparent_temperature,weather_code,wind_speed_10m,wind_direction_10m,precipitation,pressure_msl,cloud_cover',
      hourly: 'temperature_2m,weather_code,precipitation_probability,wind_speed_10m,wind_direction_10m,cloud_cover,visibility',
      daily: 'temperature_2m_max,temperature_2m_min,weather_code,sunrise,sunset,uv_index_max,precipitation_sum,precipitation_probability_max,wind_speed_10m_max,wind_direction_10m_dominant',
      timezone: 'America/New_York'
    });
    
    const weatherResponse = await fetchWithTimeout(weatherUrl, CONFIG.WEATHER_API_TIMEOUT);
    
    if (!weatherResponse.ok) {
      const errorText = await weatherResponse.text();
      throw new Error(`Weather API error: ${weatherResponse.status} - ${errorText}`);
    }
    
    const weatherData = await weatherResponse.json();
    
    // Validate weather data
    if (!weatherData || !weatherData.current) {
      throw new Error('Invalid weather data received');
    }
    
    return weatherData;
  }

  // Fetch tide data from NOAA
  async fetchTideData(station, startDate, endDate) {
    try {
      const tideUrl = buildTideUrl(station, startDate, endDate);
      const tideResponse = await fetchWithTimeout(tideUrl, CONFIG.NOAA_API_TIMEOUT);
      
      if (tideResponse.ok) {
        const tideData = await tideResponse.json();
        // For high_low product, the data is in 'data' not 'predictions'
        return tideData.data || tideData.predictions || [];
      }
    } catch (error) {
      // Tide fetch error silently handled
    }
    return [];
  }

  /**
   * Fetch water temperature from NOAA with fallback stations
   * Cape San Blas sensor is often offline, so we try nearby stations
   * @param {string} primaryStation - Primary NOAA station ID
   * @returns {number|null} Water temperature in Fahrenheit or null if unavailable
   */
  async fetchWaterTemp(primaryStation) {
    // List of nearby stations to try in order of proximity to Cape San Blas
    // Each station is progressively further away but still provides relevant data
    const stations = [
      { id: primaryStation, name: 'Cape San Blas' }, // 8728942 - Cape San Blas (primary) - often offline
      { id: '8729108', name: 'Panama City Beach' }, // Panama City Beach (backup 1) - 30 miles east
      { id: '8729210', name: 'Panama City' }, // Panama City (backup 2) - 35 miles east
      { id: '8728690', name: 'Apalachicola' }, // Apalachicola (backup 3) - 20 miles west
      { id: '8727520', name: 'Cedar Key' }, // Cedar Key (backup 4) - 100 miles southeast
    ];

    // Try each station in order until we get valid data
    for (const station of stations) {
      try {
        const waterTempUrl = buildWaterTempUrl(station.id);
        const waterTempResponse = await fetchWithTimeout(waterTempUrl, CONFIG.NOAA_API_TIMEOUT);
        
        if (waterTempResponse.ok) {
          const waterTempData = await waterTempResponse.json();
          const temp = waterTempData.data?.[0]?.v;
          
          // Validate we got a reasonable temperature value
          if (temp !== null && temp !== undefined) {
            return { temperature: temp, station: station.name, stationId: station.id };
          }
        }
      } catch (error) {
        // Station unavailable, try next one
        continue;
      }
    }
    
    // If all stations fail, return null (will show "--" in UI)
    return null;
  }

  // Process moon phase data
  processMoonData(weatherData) {
    const today = new Date();
    const todayMoonPhase = getMoonPhase(today);
    const todayMoonTimes = getMoonTimes(today);
    
    const moonDataForWeek = weatherData.daily?.time?.map((date) => {
      const dateObj = new Date(date);
      const moonTimes = getMoonTimes(dateObj);
      return {
        date: dateObj,
        phase: moonTimes.phase,
        moonrise: moonTimes.moonrise,
        moonset: moonTimes.moonset
      };
    }) || [];
    
    return {
      current: {
        ...todayMoonPhase,
        moonrise: todayMoonTimes.moonrise,
        moonset: todayMoonTimes.moonset
      },
      week: moonDataForWeek
    };
  }

  // Main method to fetch all weather data
  async fetchAllWeatherData() {
    // Check cache first
    const cachedData = await this.checkCache();
    if (cachedData) {
      return cachedData;
    }

    // Fetch new data
    const { lat, lon } = CONFIG.CAPE_SAN_BLAS_COORDS;
    const noaaStation = CONFIG.NOAA_STATION;
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + 3); // Get 3 days of tide data

    try {
      // Fetch weather data
      const weatherData = await this.fetchWeatherData(lat, lon);
      
      // Fetch tide data
      const tides = await this.fetchTideData(
        noaaStation,
        formatDateForAPI(today),
        formatDateForAPI(endDate)
      );
      
      // Fetch water temperature with fallback stations
      const waterTempData = await this.fetchWaterTemp(noaaStation);
      
      // Fetch current water level
      let currentWaterLevel = null;
      try {
        const waterLevelUrl = buildWaterLevelUrl(noaaStation);
        const waterLevelResponse = await fetchWithTimeout(waterLevelUrl, CONFIG.NOAA_API_TIMEOUT);
        if (waterLevelResponse.ok) {
          const waterLevelData = await waterLevelResponse.json();
          currentWaterLevel = waterLevelData.data?.[0]?.v;
        }
      } catch (error) {
        // Water level fetch error silently handled
      }
      
      // Process moon data
      const moonData = this.processMoonData(weatherData);
      
      // Process and cache the data
      const processedData = {
        weather: weatherData,
        tides,
        waterTemp: waterTempData?.temperature || null,
        waterTempStation: waterTempData?.station || null,
        currentWaterLevel,
        moonData,
        timestamp: Date.now()
      };
      
      // Try to cache, but don't fail if localStorage is full
      try {
        setToStorage(CONFIG.CACHE_KEY, processedData);
      } catch (e) {
        // Cache write error silently handled
      }
      
      return processedData;
    } catch (error) {
      throw error;
    }
  }
}

export default new WeatherService();